"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TopologicalPointCollectionCache = void 0;
const tslib_1 = require("tslib");
const core_shared_1 = require("@ikegps/core-shared");
const lodash_1 = require("lodash");
const mobx_1 = require("mobx");
const store_1 = require("../../store");
class Cacheable {
    constructor() {
        this.cache = {};
    }
    push(key, value, ttlMs) {
        this.cache[key] = value;
    }
    flush() {
        this.cache = {};
    }
    drop(key) {
        this.cache = (0, lodash_1.omit)(this.cache, key);
    }
    read(key) {
        return this.cache[key] || null;
    }
    view(transformer) {
        return (key) => {
            const existing = this.read(key);
            if (existing) {
                return existing;
            }
            const result = transformer(key);
            if (result) {
                this.push(key, result);
            }
            return this.read(key);
        };
    }
    all() {
        return Object.freeze(Object.assign({}, this.cache));
    }
}
tslib_1.__decorate([
    mobx_1.observable
], Cacheable.prototype, "cache", void 0);
tslib_1.__decorate([
    mobx_1.action
], Cacheable.prototype, "push", null);
tslib_1.__decorate([
    mobx_1.action
], Cacheable.prototype, "flush", null);
tslib_1.__decorate([
    mobx_1.action
], Cacheable.prototype, "drop", null);
class TopologicalPointCollectionCache {
    static getCollectionVectors(collectionOrRevision) {
        // Handle both CollectionModel and string (revision) inputs
        const collection = typeof collectionOrRevision === 'string' ? store_1.AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;
        if (!collection) {
            return [];
        }
        // Check cache first
        const cached = TopologicalPointCollectionCache.vectors.read(collection.revision);
        if (cached) {
            return cached;
        }
        const source = collection.location;
        if (!source) {
            return [];
        }
        const output = [];
        collection.forEachField((field) => {
            if (!field.value) {
                return;
            }
            if (core_shared_1.CollectionModelFieldVector.isVectorField(field)) {
                const value = field.value;
                const target = core_shared_1.IkeGeodesic.projectLocation(source, value.bearing, value.distance);
                output.push({ field, source, target });
            }
            if (core_shared_1.CollectionModelFieldAnchor.isAnchorField(field)) {
                const { distance, bearing } = field.distanceBearingPitch(source);
                if (distance === 0) {
                    return;
                }
                const target = core_shared_1.IkeGeodesic.projectLocation(source, bearing, distance);
                if (target && target.latitude && target.longitude) {
                    output.push({ field, source, target });
                }
            }
        });
        // Cache the result
        TopologicalPointCollectionCache.vectors.push(collection.revision, output);
        return output;
    }
    static getCollectionLocation(collectionOrRevision) {
        // Handle both CollectionModel and string (revision) inputs
        const collection = typeof collectionOrRevision === 'string' ? store_1.AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;
        if (!collection) {
            return null;
        }
        // Check cache first
        const cached = TopologicalPointCollectionCache.locations.read(collection.revision);
        if (cached) {
            return cached;
        }
        // Get location and cache it
        const location = collection.location;
        if (location) {
            TopologicalPointCollectionCache.locations.push(collection.revision, location);
        }
        return location;
    }
    static getCollectionPoleAccuracy(collectionOrRevision) {
        // Handle both CollectionModel and string (revision) inputs
        const collection = typeof collectionOrRevision === 'string' ? store_1.AppStore.Collection.get(collectionOrRevision) : collectionOrRevision;
        if (!collection) {
            return 'default';
        }
        // Check cache first
        const cached = TopologicalPointCollectionCache.poleAccuracy.read(collection.revision);
        if (cached) {
            return cached;
        }
        // Calculate pole accuracy
        const field = collection.locationModelField;
        let poleAccuracy = 'default';
        if (core_shared_1.CollectionModelFieldLocation.isLocationField(field)) {
            switch (field.provider) {
                case 'truesize':
                    poleAccuracy = 'low';
                    break;
                case 'gps':
                    let accuracy = field.value.accuracy;
                    if (field.raw.fix === 'rtkfixed') {
                        poleAccuracy = accuracy > 0.1 ? 'rtk_float' : 'rtk_fixed';
                    }
                    else if (field.raw.fix === 'rtkfloat') {
                        poleAccuracy = accuracy > 1 ? 'low' : 'rtk_float';
                    }
                    else {
                        poleAccuracy = 'low';
                    }
                    break;
                default:
                    poleAccuracy = field.provider;
            }
        }
        else {
            poleAccuracy = 'low';
        }
        // Cache the result
        TopologicalPointCollectionCache.poleAccuracy.push(collection.revision, poleAccuracy);
        return poleAccuracy;
    }
    static dropCollectionVectors(revision) {
        TopologicalPointCollectionCache.vectors.drop(revision);
    }
    static dropCollectionLocations(revision) {
        TopologicalPointCollectionCache.dropCollectionVectors(revision);
        TopologicalPointCollectionCache.locations.drop(revision);
        TopologicalPointCollectionCache.dropCollectionPoleAccuracy(revision);
    }
    static dropCollectionPoleAccuracy(revision) {
        TopologicalPointCollectionCache.poleAccuracy.drop(revision);
    }
    static addVector(sourceRevision, field) {
        const source = TopologicalPointCollectionCache.getCollectionLocation(sourceRevision);
        const { value: { bearing, distance }, } = field;
        const target = core_shared_1.IkeGeodesic.projectLocation(source, bearing, distance);
        const vectors = TopologicalPointCollectionCache.getCollectionVectors(sourceRevision);
        vectors.push({ field, source, target });
        TopologicalPointCollectionCache.vectors.push(sourceRevision, vectors);
    }
    static flushVectors() {
        this.vectors.flush();
    }
}
exports.TopologicalPointCollectionCache = TopologicalPointCollectionCache;
TopologicalPointCollectionCache.vectors = new Cacheable();
TopologicalPointCollectionCache.locations = new Cacheable();
TopologicalPointCollectionCache.poleAccuracy = new Cacheable();
//# sourceMappingURL=point.collection.cache.js.map